'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { safeRedirectPath } from '@/lib/auth/utils';

interface FormData {
  email: string;
  password: string;
}

export default function VendorLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signIn, signOut, resolveRole } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const role = await resolveRole(result.user);

      if (role !== 'vendor' && role !== 'admin') {
        await signOut();
        toast.error('This account is not a vendor account. Use the correct portal tab.');
        return;
      }

      let redirectTo = '/vendor-portal';
      const userId = result?.user?.id;

      if (userId && role === 'vendor') {
        const { data: vendor } = await supabase
          .from('vendors')
          .select('onboarding_completed')
          .eq('user_id', userId)
          .maybeSingle();

        if (vendor && vendor.onboarding_completed === false) {
          redirectTo = '/vendor-onboarding';
        }
      } else if (role === 'admin') {
        redirectTo = '/admin-dashboard';
      }

      toast.success(
        redirectTo === '/vendor-onboarding'
          ? "Welcome! Let's get you set up..."
          : 'Welcome! Redirecting to your Vendor Portal...'
      );
      router.push(safeRedirectPath(searchParams.get('redirect'), redirectTo));
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Invalid credentials. Please check your email and password.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-foreground">Vendor Portal Sign-In</h2>
        <p className="text-sm text-muted-foreground mt-1">View assigned jobs, accept offers & track payouts.</p>
      </div>

      <div className="mb-5 rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 flex gap-2">
        <Info size={14} className="text-accent shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Vendor accounts are <span className="text-accent font-semibold">automatically created</span> when you accept a job offer from DJ ExXtra. Login credentials are sent via email and SMS upon acceptance.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
          <input
            {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/, message: 'Invalid email' } })}
            type="email"
            autoComplete="email"
            className="w-full px-3 py-2.5 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-accent/50 transition-colors"
            placeholder="vendor@yourbusiness.com"
          />
          {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
          <p className="text-xs text-muted-foreground mb-1.5">Use the password sent to you when your account was created.</p>
          <div className="relative">
            <input
              {...register('password', { required: 'Password is required' })}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="w-full px-3 py-2.5 pr-10 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:border-accent/50 transition-colors"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2 active:scale-95"
          style={{ background: 'var(--neon-cyan)', color: '#000' }}
        >
          {loading ? <><Loader2 size={15} className="animate-spin" /> Signing in...</> : 'Sign In to Vendor Portal'}
        </button>

        <button type="button" className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors text-center">
          Forgot password? Contact DJ ExXtra at 219-614-9534
        </button>
      </form>
    </div>
  );
}
